import {
  OPEN_TASK_STATUSES,
  TASK_SORTS,
  TASK_STATUSES,
  type TaskDueFilter,
  type TaskFormOptions,
  type TaskScope,
  type TaskSort,
  type TaskStatus,
} from '@fos/shared/contracts/work';
import { TASK_SORT_LABELS, TASK_STATUS_LABELS } from '@fos/shared/labels';
import { Archive, ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { cn } from '../../../lib/cn';
import { Button } from '../../../ui/Button';
import { CountPill } from '../../../ui/CountPill';
import { Sheet } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import {
  Menu,
  MenuContent,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from '../../../ui/Menu';
import { Select } from '../../../ui/Select';
import { type AssigneeFilter, countActiveFilters, hasExtraFilters, type TaskFilterState } from '../lib/filters';
import { ClientDot } from './NewTaskDialog';

/**
 * Filtry zadań. W nagłówku (Figma): „Wszyscy klienci ▾” i przycisk „Filtry”; pasek z pozostałymi filtrami
 * (szukaj, projekt, wykonawca, termin, a na liście także status, sortowanie i archiwum) pokazuje się po kliknięciu
 * albo gdy któryś filtr jest aktywny. Stan filtrów jest w adresie strony (lib/filters.ts).
 */

type Patch = (patch: Partial<TaskFilterState>) => void;

/** Aktywny filtr (lifting 17.10.2026): lekki akcent zamiast samego pogrubienia — widać od razu, co zawęża listę. */
const ACTIVE_FILTER = 'bg-accent-subtle text-accent hover:bg-accent-subtle hover:text-accent';

function FilterMenu({
  label,
  active,
  children,
  ariaLabel,
}: {
  label: ReactNode;
  active?: boolean;
  children: ReactNode;
  ariaLabel?: string;
}) {
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button
          variant="ghost"
          size="s"
          iconRight={ChevronDown}
          aria-label={ariaLabel}
          className={cn(active && ACTIVE_FILTER)}
        >
          {label}
        </Button>
      </MenuTrigger>
      <MenuContent className="max-h-[min(420px,var(--radix-dropdown-menu-content-available-height))] overflow-y-auto">
        {children}
      </MenuContent>
    </Menu>
  );
}

/** „Wszyscy klienci ▾” (nagłówek strony, Figma 3019:2455). */
export function ClientFilter({
  value,
  options,
  onChange,
}: {
  value: string | null | undefined;
  options: TaskFormOptions | undefined;
  onChange: (clientId: string | null | undefined) => void;
}) {
  const selected = value ? options?.clients.find((c) => c.id === value) : undefined;
  const label = value === null ? 'Bez klienta' : (selected?.name ?? 'Wszyscy klienci');
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button
          variant="ghost"
          iconRight={ChevronDown}
          className={cn('max-w-[240px]', value !== undefined && ACTIVE_FILTER)}
        >
          <span className="truncate">{label}</span>
        </Button>
      </MenuTrigger>
      <MenuContent
        align="end"
        className="max-h-[min(420px,var(--radix-dropdown-menu-content-available-height))] w-60 overflow-y-auto"
      >
        <MenuRadioGroup
          value={value === undefined ? 'all' : value === null ? 'none' : value}
          onValueChange={(v) => onChange(v === 'all' ? undefined : v === 'none' ? null : v)}
        >
          <MenuRadioItem value="all">Wszyscy klienci</MenuRadioItem>
          {(options?.clients ?? []).map((c) => (
            <MenuRadioItem key={c.id} value={c.id}>
              <span className="flex items-center gap-2">
                <ClientDot color={c.color} />
                {c.name}
              </span>
            </MenuRadioItem>
          ))}
          <MenuSeparator />
          <MenuRadioItem value="none">Bez klienta (wewnętrzne)</MenuRadioItem>
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}

/** Przycisk „Filtry” (pokazuje / chowa pasek). */
export function FiltersToggle({ filters, onPatch }: { filters: TaskFilterState; onPatch: Patch }) {
  const extra = hasExtraFilters(filters);
  const count = countActiveFilters(filters);
  return (
    <Button
      variant="ghost"
      icon={SlidersHorizontal}
      aria-pressed={filters.toolbar || extra}
      onClick={() => onPatch({ toolbar: !filters.toolbar })}
      className={cn((filters.toolbar || extra) && 'bg-soft-hover text-fg')}
    >
      Filtry
      {count > 0 && <CountPill count={count} tone="accent" label={`, aktywne: ${count}`} />}
    </Button>
  );
}

const DUE_CHOICES: { value: string; label: string; due: TaskDueFilter[] }[] = [
  { value: 'all', label: 'Każdy termin', due: [] },
  { value: 'overdue', label: 'Po terminie', due: ['overdue'] },
  { value: 'today', label: 'Dziś', due: ['today'] },
  { value: 'today-overdue', label: 'Dziś i po terminie', due: ['overdue', 'today'] },
  { value: 'week', label: 'Ten tydzień', due: ['week'] },
  { value: 'none', label: 'Bez terminu', due: ['none'] },
];

function dueChoice(due: TaskDueFilter[]): string {
  const key = [...due].sort().join(',');
  return DUE_CHOICES.find((c) => [...c.due].sort().join(',') === key)?.value ?? 'all';
}

function assigneeValue(a: AssigneeFilter): string {
  return a.kind === 'user' ? a.userId : a.kind;
}

/** Pasek filtrów pod nagłówkiem. */
export function FilterBar({
  filters,
  onPatch,
  options,
  view,
  scope,
  me,
}: {
  filters: TaskFilterState;
  onPatch: Patch;
  options: TaskFormOptions | undefined;
  view: 'lista' | 'tablica';
  scope: TaskScope;
  me: string;
}) {
  const [q, setQ] = useState(filters.q);
  useEffect(() => setQ(filters.q), [filters.q]);
  // Szukanie z krótkim opóźnieniem (bez zapytania na każdy znak).
  // biome-ignore lint/correctness/useExhaustiveDependencies: reakcja tylko na wpisany tekst
  useEffect(() => {
    if (q === filters.q) return;
    const id = window.setTimeout(() => onPatch({ q }), 300);
    return () => window.clearTimeout(id);
  }, [q]);

  const projects = (options?.projects ?? []).filter((p) =>
    filters.clientId === undefined
      ? true
      : filters.clientId === null
        ? p.clientId === null
        : p.clientId === filters.clientId,
  );
  const project = filters.projectId ? options?.projects.find((p) => p.id === filters.projectId) : undefined;
  const members = (options?.members ?? []).filter((m) => m.canTasks);
  const a = filters.assignee;
  const assigneeLabel =
    a.kind === 'scope'
      ? scope === 'mine'
        ? 'Moje'
        : 'Wszyscy wykonawcy'
      : a.kind === 'all'
        ? 'Wszyscy wykonawcy'
        : a.kind === 'me'
          ? 'Ja'
          : a.kind === 'delegated'
            ? 'Zlecone przeze mnie'
            : a.kind === 'none'
              ? 'Bez wykonawcy'
              : (members.find((m) => m.userId === a.userId)?.name ?? 'Wykonawca');
  const due = dueChoice(filters.due);
  const statusValue =
    filters.status.length === 0
      ? 'open'
      : filters.status.length === TASK_STATUSES.length
        ? 'all'
        : filters.status.length === 1
          ? (filters.status[0] as string)
          : 'open';
  const statusLabel =
    statusValue === 'open'
      ? 'Otwarte'
      : statusValue === 'all'
        ? 'Wszystkie statusy'
        : TASK_STATUS_LABELS[statusValue as TaskStatus];

  return (
    <fieldset className="flex flex-wrap items-center gap-1.5">
      <legend className="sr-only">Filtry zadań</legend>
      <div className="mr-1 w-full sm:w-[220px]">
        <Input
          size="s"
          icon={Search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onPatch({ q });
          }}
          placeholder="Szukaj w tytułach albo #numer"
          aria-label="Szukaj zadań"
          type="search"
        />
      </div>

      <FilterMenu
        label={project?.name ?? (filters.projectId === null ? 'Bez projektu' : 'Projekt')}
        active={filters.projectId !== undefined}
      >
        <MenuRadioGroup
          value={filters.projectId === undefined ? 'all' : filters.projectId === null ? 'none' : filters.projectId}
          onValueChange={(v) => onPatch({ projectId: v === 'all' ? undefined : v === 'none' ? null : v })}
        >
          <MenuRadioItem value="all">Wszystkie projekty</MenuRadioItem>
          <MenuRadioItem value="none">Bez projektu</MenuRadioItem>
          {projects.length > 0 && <MenuSeparator />}
          {projects.map((p) => (
            <MenuRadioItem key={p.id} value={p.id}>
              {p.name}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </FilterMenu>

      <FilterMenu label={assigneeLabel} active={a.kind !== 'scope'} ariaLabel={`Wykonawca: ${assigneeLabel}`}>
        <MenuRadioGroup
          value={assigneeValue(a)}
          onValueChange={(v) =>
            onPatch({
              assignee:
                v === 'scope' || v === 'all' || v === 'me' || v === 'none' || v === 'delegated'
                  ? ({ kind: v } as AssigneeFilter)
                  : { kind: 'user', userId: v },
            })
          }
        >
          <MenuRadioItem value="scope">{scope === 'mine' ? 'Moje' : 'Zespół'} (jak w menu)</MenuRadioItem>
          <MenuRadioItem value="all">Wszyscy wykonawcy</MenuRadioItem>
          <MenuRadioItem value="me">Ja</MenuRadioItem>
          <MenuRadioItem value="delegated">Zlecone przeze mnie (przypisane innym)</MenuRadioItem>
          <MenuRadioItem value="none">Bez wykonawcy</MenuRadioItem>
          {members.filter((m) => m.userId !== me).length > 0 && <MenuSeparator />}
          {members
            .filter((m) => m.userId !== me)
            .map((m) => (
              <MenuRadioItem key={m.userId} value={m.userId}>
                {m.name}
              </MenuRadioItem>
            ))}
        </MenuRadioGroup>
      </FilterMenu>

      <FilterMenu
        label={due === 'all' ? 'Termin' : (DUE_CHOICES.find((c) => c.value === due)?.label ?? 'Termin')}
        active={due !== 'all'}
      >
        <MenuRadioGroup
          value={due}
          onValueChange={(v) => onPatch({ due: DUE_CHOICES.find((c) => c.value === v)?.due ?? [] })}
        >
          {DUE_CHOICES.map((c) => (
            <MenuRadioItem key={c.value} value={c.value}>
              {c.label}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </FilterMenu>

      {view === 'lista' && (
        <>
          <FilterMenu label={statusLabel} active={statusValue !== 'open'}>
            <MenuRadioGroup
              value={statusValue}
              onValueChange={(v) =>
                onPatch({ status: v === 'open' ? [] : v === 'all' ? [...TASK_STATUSES] : [v as TaskStatus] })
              }
            >
              <MenuRadioItem value="open">Otwarte</MenuRadioItem>
              <MenuRadioItem value="all">Wszystkie statusy</MenuRadioItem>
              <MenuSeparator />
              {TASK_STATUSES.map((s) => (
                <MenuRadioItem key={s} value={s}>
                  {TASK_STATUS_LABELS[s]}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </FilterMenu>
          <FilterMenu label={`Sortuj: ${TASK_SORT_LABELS[filters.sort].toLowerCase()}`} active={filters.sort !== 'due'}>
            <MenuLabel>Sortuj według</MenuLabel>
            <MenuRadioGroup value={filters.sort} onValueChange={(v) => onPatch({ sort: v as TaskSort })}>
              {TASK_SORTS.map((s) => (
                <MenuRadioItem key={s} value={s}>
                  {TASK_SORT_LABELS[s]}
                </MenuRadioItem>
              ))}
            </MenuRadioGroup>
          </FilterMenu>
          <Button
            variant="ghost"
            size="s"
            icon={Archive}
            aria-pressed={filters.archived}
            className={cn(filters.archived && ACTIVE_FILTER)}
            onClick={() => onPatch({ archived: !filters.archived })}
          >
            Archiwum
          </Button>
        </>
      )}

      {hasExtraFilters(filters) && (
        <Button
          variant="ghost"
          size="s"
          icon={X}
          onClick={() =>
            onPatch({
              projectId: undefined,
              serviceId: undefined,
              assignee: { kind: 'scope' },
              due: [],
              status: [],
              q: '',
              archived: false,
            })
          }
        >
          Wyczyść
        </Button>
      )}
    </fieldset>
  );
}

/** Statusy listy wg filtra (domyślnie otwarte). */
export function statusesFor(filters: TaskFilterState): TaskStatus[] {
  return filters.status.length > 0 ? filters.status : [...OPEN_TASK_STATUSES];
}

const CLEAR_FILTERS: Partial<TaskFilterState> = {
  clientId: undefined,
  projectId: undefined,
  serviceId: undefined,
  assignee: { kind: 'scope' },
  due: [],
  status: [],
  q: '',
  archived: false,
  sort: 'due',
};

/**
 * Filtry na telefonie (lifting 17.10.2026, DECISIONS „Telefon: łatwy dostęp do całego menu” — „filtry w panelu”):
 * dolny panel (Sheet) z polami jeden pod drugim, zmiana od razu filtruje listę (stan w adresie jak na desktopie).
 * Lista: szukaj, klient, projekt, wykonawca, status i sortowanie (termin wybierają zakładki Dziś / Tydzień /
 * Wszystkie); tablica: szukaj, klient, projekt, wykonawca i termin.
 */
export function MobileFilterSheet({
  open,
  onOpenChange,
  filters,
  onPatch,
  options,
  view,
  scope,
  me,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TaskFilterState;
  onPatch: Patch;
  options: TaskFormOptions | undefined;
  view: 'lista' | 'tablica';
  scope: TaskScope;
  me: string;
}) {
  const [q, setQ] = useState(filters.q);
  useEffect(() => setQ(filters.q), [filters.q]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reakcja tylko na wpisany tekst
  useEffect(() => {
    if (q === filters.q) return;
    const id = window.setTimeout(() => onPatch({ q }), 300);
    return () => window.clearTimeout(id);
  }, [q]);

  const clients = options?.clients ?? [];
  const projects = (options?.projects ?? []).filter((p) =>
    filters.clientId === undefined
      ? true
      : filters.clientId === null
        ? p.clientId === null
        : p.clientId === filters.clientId,
  );
  const members = (options?.members ?? []).filter((m) => m.canTasks && m.userId !== me);
  const statusValue =
    filters.status.length === 0
      ? 'open'
      : filters.status.length === TASK_STATUSES.length
        ? 'all'
        : (filters.status[0] as string);
  const count = countActiveFilters(filters, { withClient: true });
  const controlClass = 'h-11';

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      side="bottom"
      title="Filtry"
      footer={
        <>
          {count > 0 && (
            <Button variant="ghost" size="l" icon={X} onClick={() => onPatch(CLEAR_FILTERS)} className="mr-auto">
              Wyczyść
            </Button>
          )}
          <Button variant="primary" size="l" onClick={() => onOpenChange(false)}>
            Pokaż zadania
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Szukaj">
          <Input
            icon={Search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tytuł albo #numer"
            type="search"
            className={controlClass}
          />
        </Field>
        <Field label="Klient">
          <Select
            className={controlClass}
            value={filters.clientId === undefined ? 'all' : filters.clientId === null ? 'none' : filters.clientId}
            onValueChange={(v) =>
              onPatch({
                clientId: v === 'all' ? undefined : v === 'none' ? null : v,
                projectId: undefined,
                serviceId: undefined,
              })
            }
            options={[
              { value: 'all', label: 'Wszyscy klienci' },
              ...clients.map((c) => ({ value: c.id, label: c.name, leading: <ClientDot color={c.color} /> })),
              { value: 'none', label: 'Bez klienta (wewnętrzne)' },
            ]}
          />
        </Field>
        <Field label="Projekt">
          <Select
            className={controlClass}
            value={filters.projectId === undefined ? 'all' : filters.projectId === null ? 'none' : filters.projectId}
            onValueChange={(v) => onPatch({ projectId: v === 'all' ? undefined : v === 'none' ? null : v })}
            options={[
              { value: 'all', label: 'Wszystkie projekty' },
              { value: 'none', label: 'Bez projektu' },
              ...projects.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        </Field>
        <Field label="Wykonawca">
          <Select
            className={controlClass}
            value={assigneeValue(filters.assignee)}
            onValueChange={(v) =>
              onPatch({
                assignee:
                  v === 'scope' || v === 'all' || v === 'me' || v === 'none' || v === 'delegated'
                    ? ({ kind: v } as AssigneeFilter)
                    : { kind: 'user', userId: v },
              })
            }
            options={[
              { value: 'scope', label: `${scope === 'mine' ? 'Moje' : 'Zespół'} (jak w menu)` },
              { value: 'all', label: 'Wszyscy wykonawcy' },
              { value: 'me', label: 'Ja' },
              { value: 'delegated', label: 'Zlecone przeze mnie' },
              { value: 'none', label: 'Bez wykonawcy' },
              ...members.map((m) => ({ value: m.userId, label: m.name })),
            ]}
          />
        </Field>
        {view === 'tablica' ? (
          <Field label="Termin">
            <Select
              className={controlClass}
              value={dueChoice(filters.due)}
              onValueChange={(v) => onPatch({ due: DUE_CHOICES.find((c) => c.value === v)?.due ?? [] })}
              options={DUE_CHOICES.map((c) => ({ value: c.value, label: c.label }))}
            />
          </Field>
        ) : (
          <>
            <Field label="Status">
              <Select
                className={controlClass}
                value={statusValue}
                onValueChange={(v) =>
                  onPatch({ status: v === 'open' ? [] : v === 'all' ? [...TASK_STATUSES] : [v as TaskStatus] })
                }
                options={[
                  { value: 'open', label: 'Otwarte' },
                  { value: 'all', label: 'Wszystkie statusy' },
                  ...TASK_STATUSES.map((s) => ({ value: s, label: TASK_STATUS_LABELS[s] })),
                ]}
              />
            </Field>
            <Field label="Sortuj według">
              <Select
                className={controlClass}
                value={filters.sort}
                onValueChange={(v) => onPatch({ sort: v as TaskSort })}
                options={TASK_SORTS.map((s) => ({ value: s, label: TASK_SORT_LABELS[s] }))}
              />
            </Field>
          </>
        )}
      </div>
    </Sheet>
  );
}
